import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Mail, Send, FileText, Loader2, Search, CheckCircle2, Wand2, MailCheck, Paperclip, AlertTriangle,
  ShieldCheck, Copy, ExternalLink, Link2, Clock, Users, Save, FileDown,
  ThumbsUp, CalendarRange, RotateCcw, Handshake, Phone, PhoneIncoming, PhoneOutgoing, AudioLines, Voicemail,
  MailX, CalendarClock, UserRound, Hourglass, MailWarning,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { frenchDate, frenchDateTime } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DatesPicker } from "@/components/DatesPicker";
import { toast } from "sonner";

const TYPES = [
  { v: "confirmation", l: "Confirmation de booking" },
  { v: "proposition", l: "Proposition de dates" },
  { v: "remerciement", l: "Remerciement (accueil magasin)" },
  { v: "rse", l: "Remerciement + rapport RSE" },
];

// Libellés lisibles pour l'historique des envois (inclut l'email de preuve auto).
const TYPE_LABELS = {
  confirmation: "Confirmation",
  proposition: "Proposition",
  remerciement: "Remerciement",
  rse: "Remerciement RSE",
  attestation: "Preuve de confirmation",
};

// Détecte les adresses email « suspectes » (fautes de frappe fréquentes) — non bloquant.
const SUSPICIOUS_DOMAIN = /(gmial|gmai\.|gmal\.|gnail|hotmial|hotmai\.|hotmal|outlok|yaho\.|yahou|orang\.fr|wanadou|\.con$|\.cmo$|\.comm$|\.cm$|\.fe$|\.frr$|\.ffr$)/i;
function emailSuspicion(email) {
  const e = (email || "").trim();
  if (!e) return null;
  if (/\s/.test(e)) return "L'adresse contient un espace.";
  if ((e.match(/@/g) || []).length !== 1) return "L'adresse doit contenir un seul « @ ».";
  const [local, domain] = e.split("@");
  if (!local || !domain) return "Format d'adresse incomplet.";
  if (!domain.includes(".")) return "Le domaine semble incomplet (point manquant).";
  if (domain.endsWith(".") || domain.startsWith(".") || local.endsWith(".") || local.startsWith(".")) return "L'adresse comporte une erreur de ponctuation.";
  if (domain.includes("..") || local.includes("..")) return "L'adresse contient deux points consécutifs.";
  if (SUSPICIOUS_DOMAIN.test(e)) return "Le domaine semble contenir une faute de frappe.";
  return null;
}

// Métadonnées d'affichage du statut de délivrabilité (webhook Resend).
const DELIVERY_META = {
  delivered: { label: "Délivré", icon: MailCheck, cls: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" },
  bounced: { label: "Non délivré", icon: MailX, cls: "bg-[rgba(214,69,69,0.14)] text-[#9B2226]" },
  complained: { label: "Signalé spam", icon: MailWarning, cls: "bg-[rgba(233,168,0,0.18)] text-[#7A5B00]" },
  delayed: { label: "Retardé", icon: Hourglass, cls: "bg-[rgba(233,168,0,0.14)] text-[#7A5B00]" },
};

export default function Emails() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [spots, setSpots] = useState([]);
  const [assocs, setAssocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [spotId, setSpotId] = useState(params.get("spot") || "");
  const [spot, setSpot] = useState(null);
  const [type, setType] = useState(params.get("type") || "confirmation");
  const [assocId, setAssocId] = useState("");
  const [facts, setFacts] = useState({ dates: "", consignes: "", nb_soutiens: "", nb_sensibilises: "" });
  const [objet, setObjet] = useState("");
  const [corps, setCorps] = useState("");
  const [html, setHtml] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeMandat, setActiveMandat] = useState(null);
  const [confirmUrl, setConfirmUrl] = useState("");
  const [ack, setAck] = useState(null);
  const [acks, setAcks] = useState([]);
  const [preparingAck, setPreparingAck] = useState(false);
  const [extraCc, setExtraCc] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [lignes, setLignes] = useState([]);
  const [ligneId, setLigneId] = useState("");
  const [isoDates, setIsoDates] = useState([]);
  const [calls, setCalls] = useState([]);
  // Vérification avant envoi (fenêtre de double-contrôle adresse + dates).
  const [sendCheckOpen, setSendCheckOpen] = useState(false);
  const [confirmEmailOk, setConfirmEmailOk] = useState(false);
  const [confirmDatesOk, setConfirmDatesOk] = useState(false);
  // Verrou anti-double-envoi : garantit qu'un seul envoi part par clic, même en
  // cas de rendus multiples ou de clics rapprochés (aucun envoi « fantôme »).
  const sendingRef = useRef(false);

  // SÉCURITÉ ANTI-ENVOI ACCIDENTEL — si l'utilisateur QUITTE l'onglet/la page
  // (aller écouter un appel, ouvrir une autre page, changer de fenêtre…), on
  // RÉINITIALISE les cases de confirmation ET on referme la fenêtre d'envoi.
  // Ainsi, au RETOUR sur la page, plus rien n'est « armé » : le bouton
  // « Confirmer et envoyer » est de nouveau désactivé tant que l'utilisateur ne
  // re-coche pas volontairement. Aucun email ne peut donc « partir tout seul ».
  useEffect(() => {
    const disarm = () => {
      setConfirmEmailOk(false);
      setConfirmDatesOk(false);
      setSendCheckOpen(false);
    };
    const onVisibility = () => { if (document.hidden) disarm(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", disarm);
    window.addEventListener("pagehide", disarm);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", disarm);
      window.removeEventListener("pagehide", disarm);
    };
  }, []);

  // Lignes disponibles pour positionner les créneaux à la confirmation :
  //  • CDP  -> uniquement SES lignes ; Admin -> toutes.
  //  • filtrées par l'association de l'email.
  const myLignes = lignes
    .filter((l) =>
      (user?.role === "admin" || l.cdp_id === user?.id) &&
      (!assocId || l.association_id === assocId))
    .sort((a, b) =>
      (a.status === "active" ? 0 : 1) - (b.status === "active" ? 0 : 1) ||
      String(a.identifiant || "").localeCompare(String(b.identifiant || "")));

  useEffect(() => {
    api.get("/lignes").then((r) => setLignes(r.data || [])).catch(() => setLignes([]));
  }, []);

  // Auto-sélection de la 1ʳᵉ ligne pertinente quand l'association change.
  useEffect(() => {
    if (myLignes.length === 0) { setLigneId(""); return; }
    if (!myLignes.some((l) => l.id === ligneId)) setLigneId(myLignes[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assocId, lignes]);

  useEffect(() => {
    if (!assocId) { setActiveMandat(null); return; }
    api.get("/mandats/active", { params: { association_id: assocId } })
      .then((r) => setActiveMandat(r.data && r.data.id ? r.data : null))
      .catch(() => setActiveMandat(null));
  }, [assocId]);

  useEffect(() => {
    api.get("/associations").then((r) => { setAssocs(r.data); setAssocId(r.data[0]?.id || ""); });
    api.get("/emails/templates").then((r) => setTemplates(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => api.get("/spots", { params: { search: search || undefined, limit: 30 } }).then((r) => setSpots(r.data)), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadSpot = useCallback(async (id) => {
    if (!id) { setSpot(null); setCalls([]); return; }
    const { data } = await api.get(`/spots/${id}`);
    setSpot(data);
    setContactEmail(data.contact_principal?.email || "");
    setFacts((p) => ({ ...p, consignes: data.consignes || "" }));
    api.get("/emails", { params: { spot_id: id } }).then((r) => setHistory(r.data));
    api.get("/ack", { params: { spot_id: id } }).then((r) => setAcks(r.data)).catch(() => setAcks([]));
    // Historique des appels/audios du spot (avec résumé IA) — chronologie récente.
    api.get("/calls", { params: { spot_id: id, limit: 30 } })
      .then((r) => setCalls((r.data?.calls) || []))
      .catch(() => setCalls([]));
  }, []);
  useEffect(() => { if (spotId) loadSpot(spotId); }, [spotId, loadSpot]);

  // Un nouveau lien d'accusé est propre à un contexte (spot / association / dates).
  // On le réinitialise dès qu'un de ces éléments change.
  useEffect(() => { setConfirmUrl(""); setAck(null); }, [spotId, assocId, facts.dates, type, ligneId]);

  // Type de mail « remerciement » : on propose au magasin de choisir ses prochaines dates.
  const isThankType = (t) => t === "remerciement" || t === "rse";
  const ackKindFor = (t) => (t === "proposition" ? "proposition" : isThankType(t) ? "renouvellement" : "confirmation");
  const urlKeyFor = (t) => (isThankType(t) ? "renew_url" : "confirmation_url");
  const needsLink = (t) => t === "confirmation" || t === "proposition" || isThankType(t);

  const buildFacts = (overrides = {}) => {
    const c = spot?.contact_principal || {};
    const assoc = assocs.find((a) => a.id === assocId);
    return {
      nom_directeur: [c.civilite, c.nom].filter(Boolean).join(" ") || "Madame, Monsieur",
      spot: spot?.normalized_name, association: assoc?.name, association_acronym: assoc?.acronym,
      association_id: assoc?.id,
      adresse: spot?.address_full || spot?.ville || "",
      cdp: user?.name,
      dates: facts.dates, horaires: "10h à 20h", consignes: facts.consignes,
      nb_soutiens: facts.nb_soutiens || undefined, nb_sensibilises: facts.nb_sensibilises || undefined,
      ...overrides,
    };
  };

  // Crée (si besoin) l'accusé de réception et renvoie l'URL publique + l'objet ack.
  const ensureAck = useCallback(async () => {
    if (confirmUrl && ack) return { url: confirmUrl, ack };
    if (!spot) return { url: "", ack: null };
    const { data } = await api.post("/ack", {
      spot_id: spotId, association_id: assocId, dates: facts.dates || undefined,
      consignes: facts.consignes || undefined,
      ligne_id: ligneId || undefined,
      kind: ackKindFor(type),
      iso_dates: isoDates.length ? isoDates : undefined,
    });
    const url = `${window.location.origin}${data.path}`;
    setConfirmUrl(url); setAck(data);
    return { url, ack: data };
  }, [confirmUrl, ack, spot, spotId, assocId, facts.dates, facts.consignes, ligneId, isoDates, type]);

  const prepareAck = async () => {
    setPreparingAck(true);
    try { await ensureAck(); toast.success("Lien de confirmation prêt à partager"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la création du lien"); }
    finally { setPreparingAck(false); }
  };

  const copyConfirmUrl = async () => {
    try { await navigator.clipboard.writeText(confirmUrl); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const emailValid = (e) => /.+@.+\..+/.test((e || "").trim());

  // Enregistre l'email du contact directement dans la fiche du spot (base de données).
  const saveContactEmail = async () => {
    if (!spot) return;
    const email = contactEmail.trim();
    if (email && !emailValid(email)) { toast.error("Adresse email invalide"); return; }
    setSavingEmail(true);
    try {
      const nextContact = { ...(spot.contact_principal || {}), email: email || null };
      await api.put(`/spots/${spotId}`, { contact_principal: nextContact });
      setSpot((p) => ({ ...p, contact_principal: nextContact }));
      toast.success(email ? "Email du contact enregistré dans la fiche du spot" : "Email du contact effacé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement de l'email");
    } finally {
      setSavingEmail(false);
    }
  };

  const generate = async () => {
    if (!spot) { toast.error("Choisissez un spot"); return; }
    setGenerating(true);
    try {
      let overrides = {};
      if (needsLink(type)) {
        const { url } = await ensureAck();
        overrides[urlKeyFor(type)] = url;
      }
      const { data } = await api.post("/emails/generate", { type, facts: buildFacts(overrides) });
      setObjet(data.objet); setCorps(data.corps); setHtml(data.html);
      toast.success("Email généré par l'IA");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const useTemplate = async () => {
    const tpl = templates.find((t) => t.key === type);
    if (!tpl) { toast.error("Aucun modèle"); return; }
    let overrides = {};
    if (needsLink(type)) {
      const { url } = await ensureAck();
      overrides[urlKeyFor(type)] = url;
    }
    const f = buildFacts(overrides);
    const apply = (s) => (s || "").replace(/\{(\w+)\}/g, (_, k) => f[k] ?? `{${k}}`);
    const o = apply(tpl.subject); const c = apply(tpl.body);
    setObjet(o); setCorps(c);
    const { data } = await api.post("/emails/render", { type, objet: o, corps: c, facts: f });
    setHtml(data.html);
    toast.success("Modèle appliqué");
  };

  const rerender = async () => {
    if (!objet && !corps) return;
    const overrides = confirmUrl ? { [urlKeyFor(type)]: confirmUrl } : {};
    const { data } = await api.post("/emails/render", { type, objet, corps, facts: buildFacts(overrides) });
    setHtml(data.html);
  };

  // Dates obligatoires pour les emails de confirmation / proposition de dates.
  const datesRequired = type === "confirmation" || type === "proposition";
  const hasDates = (facts.dates || "").trim().length > 0 || isoDates.length > 0;

  // Ouvre la fenêtre de vérification (au lieu d'envoyer directement).
  const openSendCheck = () => {
    if (!spot || !html) { toast.error("Générez d'abord l'email"); return; }
    const to = contactEmail.trim();
    if (!to || !emailValid(to)) { toast.error("Renseignez un email de contact valide (puis « Enregistrer »)"); return; }
    if (datesRequired && !hasDates) { toast.error("Sélectionnez au moins une date de passage avant l'envoi."); return; }
    setConfirmEmailOk(false);
    setConfirmDatesOk(false);
    setSendCheckOpen(true);
  };

  // Aide « Corriger l'adresse » : cible et sélectionne le champ email du contact.
  const focusContactEmail = () => {
    setSendCheckOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="email-contact-input"]');
      if (el) { el.focus(); el.select?.(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
      toast.info("Corrigez l'adresse email, « Enregistrer », puis régénérez et renvoyez l'email.");
    }, 60);
  };

  const doSend = async () => {
    if (sendingRef.current) return;                 // verrou : un envoi déjà en cours
    if (!spot || !html) { toast.error("Générez d'abord l'email"); return; }
    const to = contactEmail.trim();
    if (!to || !emailValid(to)) { toast.error("Renseignez un email de contact valide (puis « Enregistrer »)"); return; }
    // Défense en profondeur : on n'envoie JAMAIS sans les confirmations explicites
    // (mêmes conditions que le bouton), même si l'appel provenait d'ailleurs.
    if (!confirmEmailOk || (datesRequired && !confirmDatesOk)) {
      toast.error("Cochez les cases de confirmation avant l'envoi.");
      return;
    }
    // Persiste l'email dans la fiche du spot s'il a été saisi/modifié sans clic « Enregistrer ».
    if (to !== (spot.contact_principal?.email || "")) {
      try {
        const nextContact = { ...(spot.contact_principal || {}), email: to };
        await api.put(`/spots/${spotId}`, { contact_principal: nextContact });
        setSpot((p) => ({ ...p, contact_principal: nextContact }));
      } catch { /* non bloquant : l'envoi peut se poursuivre */ }
    }
    const secondary = (spot.contacts_secondaires || []).map((x) => x.email).filter(Boolean);
    const extra = extraCc.split(/[,;\s]+/).map((s) => s.trim()).filter((e) => /.+@.+\..+/.test(e));
    const cc = Array.from(new Set([...secondary, ...extra])).filter((e) => e && e !== to);
    sendingRef.current = true;
    setSending(true);
    try {
      // Garantit le CTA (lien confirmation / « prochaines dates ») dans le mail ENVOYÉ :
      // on re-rend côté serveur avec le lien à jour (évite un HTML d'aperçu obsolète).
      let finalHtml = html;
      let ackId = ack?.id;
      try {
        let ov = {};
        if (needsLink(type)) {
          const { url, ack: a } = await ensureAck();
          ov[urlKeyFor(type)] = url;
          if (a?.id) ackId = a.id;
        }
        const { data: rr } = await api.post("/emails/render", { type, objet, corps, facts: buildFacts(ov) });
        if (rr?.html) finalHtml = rr.html;
      } catch { /* on retombe sur l'aperçu courant */ }
      const { data } = await api.post("/emails/send", { spot_id: spotId, association_id: assocId, type, to, cc, reply_to: user?.email, subject: objet, body_html: finalHtml, ack_id: ackId });
      toast.success(data.attachment ? `Email envoyé à ${to} — mandat « ${data.attachment} » joint` : `Email envoyé à ${to} (Reply-To : ${user?.email})`);
      if (data.mandat_warning) toast.warning(data.mandat_warning, { duration: 8000 });
      setSendCheckOpen(false);
      setConfirmEmailOk(false);
      setConfirmDatesOk(false);
      api.get("/emails", { params: { spot_id: spotId } }).then((r) => setHistory(r.data));
      api.get("/ack", { params: { spot_id: spotId } }).then((r) => setAcks(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'envoi"); }
    finally { setSending(false); sendingRef.current = false; }
  };

  const markConfirmed = async (id) => {
    await api.post(`/emails/${id}/mark-confirmed`);
    toast.success("Confirmation écrite enregistrée");
    api.get("/emails", { params: { spot_id: spotId } }).then((r) => setHistory(r.data));
    loadSpot(spotId);
  };

  // --- Négociation de dates (côté interne) ---------------------------------
  const [reproposeFor, setReproposeFor] = useState(null); // id de l'accusé en cours de reproposition
  const [rpDates, setRpDates] = useState("");
  const [rpIso, setRpIso] = useState([]);
  const [rpNote, setRpNote] = useState("");
  const [ackBusy, setAckBusy] = useState(null); // id en cours de traitement

  const reloadAcks = () =>
    api.get("/ack", { params: { spot_id: spotId } })
      .then((r) => { setAcks(r.data); window.dispatchEvent(new Event("shariti:acks-updated")); })
      .catch(() => {});

  const approveAck = async (id) => {
    setAckBusy(id);
    try {
      await api.post(`/ack/${id}/approve`);
      toast.success("Dates approuvées — positionnées en « Ferme » sur le planning");
      await reloadAcks();
      loadSpot(spotId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setAckBusy(null);
    }
  };

  const openRepropose = (id) => { setReproposeFor(id); setRpDates(""); setRpIso([]); setRpNote(""); };

  const submitRepropose = async (id) => {
    if (!rpDates.trim() && rpIso.length === 0) { toast.error("Choisissez au moins une date"); return; }
    setAckBusy(id);
    try {
      await api.post(`/ack/${id}/repropose`, {
        dates: rpDates.trim() || undefined,
        iso_dates: rpIso.length ? rpIso : undefined,
        note: rpNote.trim() || undefined,
      });
      toast.success("Nouvelle proposition envoyée au directeur");
      setReproposeFor(null);
      await reloadAcks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la reproposition");
    } finally {
      setAckBusy(null);
    }
  };

  return (
    <>
      <PageHeader title="Emails & automatisations"
        subtitle="Rédigez des emails professionnels assistés par IA (confirmation, proposition, rapport RSE), en un clic."
        action={
          <Link to="/negociations"
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-soft)] bg-white px-3.5 py-2 text-sm font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]"
            data-testid="emails-to-negociations">
            <Handshake className="h-4 w-4 text-[var(--shariti-orange)]" /> Suivi des négociations
          </Link>
        } />
      <div className="mx-auto grid max-w-[1280px] gap-4 px-4 py-4 sm:px-6 @4xl/page:grid-cols-2 lg:px-8">
        <div className="space-y-4">
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]">
            <h3 className="mb-3 text-sm font-bold">Composer</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Spot destinataire</Label>
                <div className="relative mt-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                  <Input value={search} onChange={(e) => { setSearch(e.target.value); setShowResults(true); }} onFocus={() => setShowResults(true)} placeholder="Rechercher un spot…" className="h-9 pl-9" data-testid="email-spot-search" />
                </div>
                {showResults && search && spots.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-[var(--border-soft)]">
                    {spots.map((s) => (
                      <button type="button" key={s.id} onClick={() => { setSpotId(s.id); setSearch(s.normalized_name); setShowResults(false); }} data-testid={`email-spot-${s.id}`}
                        className="block w-full px-3 py-1.5 text-left text-xs hover:bg-[var(--surface-2)]">{s.normalized_name} <span className="text-muted-ink">{s.contact_principal?.email || "sans email"}</span></button>
                    ))}
                  </div>
                )}
              </div>
              {spot && (
                <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2.5 text-xs" data-testid="email-contact-block">
                  <div className="font-bold">{spot.normalized_name}</div>
                  <Label className="mt-2 flex items-center gap-1 text-[11px] text-muted-ink">
                    <Mail className="h-3 w-3" /> Email du contact (destinataire)
                  </Label>
                  <div className="mt-1 flex items-center gap-1.5">
                    <Input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="directeur@magasin.fr"
                      className="h-8 flex-1 bg-white text-xs"
                      data-testid="email-contact-input"
                    />
                    <Button
                      onClick={saveContactEmail}
                      disabled={savingEmail || contactEmail.trim() === (spot.contact_principal?.email || "")}
                      size="sm"
                      variant="outline"
                      className="h-8 shrink-0 border-[var(--border-soft)] bg-white px-2.5 text-xs"
                      data-testid="email-contact-save"
                    >
                      {savingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="mr-1 h-3.5 w-3.5" /> Enregistrer</>}
                    </Button>
                  </div>
                  {!spot.contact_principal?.email && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-[#8A6D00]" data-testid="email-contact-missing">
                      <AlertTriangle className="h-3 w-3 shrink-0" /> Aucun email enregistré — saisissez-le puis « Enregistrer ».
                    </div>
                  )}
                  <div className="mt-1.5 text-[11px] text-muted-ink">Reply-To : {user?.email}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Type d'email</Label>
                  <Select value={type} onValueChange={setType}><SelectTrigger className="mt-1" data-testid="email-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Association</Label>
                  <Select value={assocId} onValueChange={setAssocId}><SelectTrigger className="mt-1" data-testid="email-assoc"><SelectValue /></SelectTrigger>
                    <SelectContent>{assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              {type === "confirmation" && (
                activeMandat ? (
                  activeMandat.status === "expired" ? (
                    <div className="flex items-start gap-2 rounded-lg border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] px-3 py-2 text-xs text-[#B23333]" data-testid="email-mandat-note">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0">Mandat joint <span className="font-semibold">expiré</span> (validité au {String(activeMandat.valid_until).slice(0, 10).split("-").reverse().join("/")}) : <span className="font-semibold">{activeMandat.filename}</span>. Demandez un mandat à jour à l'association (Admin › Mandats). L'email peut tout de même être envoyé.</span>
                    </div>
                  ) : activeMandat.status === "expiring_soon" ? (
                    <div className="flex items-start gap-2 rounded-lg border border-[rgba(233,184,0,0.5)] bg-[rgba(233,184,0,0.12)] px-3 py-2 text-xs text-[#7A5A00]" data-testid="email-mandat-note">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0">Mandat joint <span className="font-semibold">bientôt expiré</span> (validité au {String(activeMandat.valid_until).slice(0, 10).split("-").reverse().join("/")}) : <span className="font-semibold">{activeMandat.filename}</span>. Pensez à demander le renouvellement.</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-lg border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.07)] px-3 py-2 text-xs text-[#0F5A2F]" data-testid="email-mandat-note">
                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 break-words">Lettre de mandat jointe : <span className="font-semibold">{activeMandat.filename}</span></span>
                    </div>
                  )
                ) : (
                  <div className="flex items-center gap-2 rounded-lg border border-[rgba(233,184,0,0.4)] bg-[rgba(233,184,0,0.1)] px-3 py-2 text-xs text-[#5A4200]" data-testid="email-mandat-note">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>Aucun mandat actif pour cette association. Ajoutez-en un dans Admin › Mandats.</span>
                  </div>
                )
              )}
              {(type === "confirmation" || type === "proposition") && spot && (
                <div className="rounded-xl border border-[rgba(238,114,46,0.28)] bg-[#FFF7F1] p-3" data-testid="ack-block">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#8A3A10]">
                    <ShieldCheck className="h-4 w-4" />
                    {type === "proposition" ? "Lien de négociation de dates" : "Accusé de réception « à chaud »"}
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-[rgba(24,24,24,0.65)]">
                    {type === "proposition"
                      ? "Le directeur pourra valider les dates proposées ou en proposer d'autres. Vous pourrez alors approuver ou reproposer (suivi dans « Accusés de réception »)."
                      : "Le directeur clique « Je confirme » pendant l'appel → nom, email et horodatage enregistrés. Le bouton est inséré dans l'email ; partagez aussi le lien en direct."}
                  </p>
                  {!confirmUrl ? (
                    <Button onClick={prepareAck} disabled={preparingAck} variant="outline"
                      className="mt-2 h-8 border-[rgba(238,114,46,0.4)] bg-white text-xs text-[#8A3A10] hover:bg-[#FFF3E9]"
                      data-testid="ack-prepare-btn">
                      {preparingAck ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Link2 className="mr-1.5 h-3.5 w-3.5" /> {type === "proposition" ? "Préparer le lien de négociation" : "Préparer le lien de confirmation"}</>}
                    </Button>
                  ) : (
                    <div className="mt-2 flex items-center gap-1.5">
                      <Input readOnly value={confirmUrl} className="h-8 flex-1 bg-white text-[11px]" data-testid="ack-url-input" />
                      <Button onClick={copyConfirmUrl} size="sm" variant="outline" className="h-8 shrink-0 border-[var(--border-soft)] px-2" data-testid="ack-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
                      <a href={confirmUrl} target="_blank" rel="noreferrer" className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]" data-testid="ack-open-btn"><ExternalLink className="h-3.5 w-3.5" /></a>
                    </div>
                  )}
                </div>
              )}
              {isThankType(type) && spot && (
                <div className="rounded-xl border border-[rgba(238,114,46,0.28)] bg-[#FFF7F1] p-3" data-testid="renew-block">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#8A3A10]">
                    <ShieldCheck className="h-4 w-4" />
                    Bouton « Choisir nos prochaines dates »
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-[rgba(24,24,24,0.65)]">
                    Un bouton d'appel à l'action est inséré dans l'email : le responsable choisit lui-même vos prochaines dates → confirmation automatique et créneaux posés sur le planning. Le lien est généré automatiquement à la génération/à l'envoi.
                  </p>
                  {confirmUrl && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <Input readOnly value={confirmUrl} className="h-8 flex-1 bg-white text-[11px]" data-testid="renew-url-input" />
                      <Button onClick={copyConfirmUrl} size="sm" variant="outline" className="h-8 shrink-0 border-[var(--border-soft)] px-2" data-testid="renew-copy-btn"><Copy className="h-3.5 w-3.5" /></Button>
                      <a href={confirmUrl} target="_blank" rel="noreferrer" className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]" data-testid="renew-open-btn"><ExternalLink className="h-3.5 w-3.5" /></a>
                    </div>
                  )}
                </div>
              )}
              {type !== "rse" ? (
                <>
                  <DatesPicker value={facts.dates} onChange={(v) => setFacts((p) => ({ ...p, dates: v }))}
                    onDatesChange={setIsoDates} testId="email-dates"
                    helperText="Choisissez les dates sur le calendrier (jour + mois écrits automatiquement) ou saisissez librement." />
                  {(type === "confirmation" || type === "proposition") && (
                    <div data-testid="email-ligne-block">
                      <Label className="text-xs">Ligne où positionner les créneaux (à la validation)</Label>
                      {myLignes.length > 0 ? (
                        <Select value={ligneId} onValueChange={setLigneId}>
                          <SelectTrigger className="mt-1 h-9" data-testid="email-ligne-select">
                            <SelectValue placeholder="Choisir une ligne" />
                          </SelectTrigger>
                          <SelectContent>
                            {myLignes.map((l) => (
                              <SelectItem key={l.id} value={l.id} data-testid={`email-ligne-opt-${l.id}`}>{l.identifiant}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-[#8A6D00]" data-testid="email-ligne-empty">
                          <AlertTriangle className="h-3 w-3 shrink-0" /> Aucune ligne pour cette association — le positionnement automatique sera ignoré.
                        </p>
                      )}
                      <p className="mt-1 text-[10px] text-muted-ink">
                        À la validation finale (confirmation ou accord de dates), les créneaux se placeront en « Ferme » sur cette ligne (spot + consignes).
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Personnes sensibilisées</Label><Input type="number" value={facts.nb_sensibilises} onChange={(e) => setFacts((p) => ({ ...p, nb_sensibilises: e.target.value }))} className="mt-1 h-9" data-testid="email-sensibilises" /></div>
                  <div><Label className="text-xs">Nouveaux soutiens</Label><Input type="number" value={facts.nb_soutiens} onChange={(e) => setFacts((p) => ({ ...p, nb_soutiens: e.target.value }))} className="mt-1 h-9" data-testid="email-soutiens" /></div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={generate} disabled={generating || !spot} className="flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="email-generate">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-2 h-4 w-4" /> Générer avec l'IA</>}
                </Button>
                <Button onClick={useTemplate} variant="secondary" disabled={!spot} className="border border-[var(--border-soft)] bg-white" data-testid="email-template-btn"><FileText className="mr-2 h-4 w-4" /> Modèle</Button>
              </div>
            </div>
          </div>

          {(objet || corps) && (
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 shadow-[var(--shadow-sm)]">
              <h3 className="mb-3 text-sm font-bold">Relire & ajuster</h3>
              <Label className="text-xs">Objet</Label>
              <Input value={objet} onChange={(e) => setObjet(e.target.value)} onBlur={rerender} className="mt-1 mb-3 h-9" data-testid="email-objet" />
              <Label className="text-xs">Corps</Label>
              <Textarea value={corps} onChange={(e) => setCorps(e.target.value)} onBlur={rerender} rows={10} className="mt-1" data-testid="email-corps" />
              <Label className="mt-3 flex items-center gap-1 text-xs"><Users className="h-3 w-3" /> En copie (facultatif)</Label>
              <Input value={extraCc} onChange={(e) => setExtraCc(e.target.value)} placeholder="2ᵉ contact du magasin — ex : adjoint@magasin.fr"
                className="mt-1 h-9" data-testid="email-cc" />
              <Button onClick={openSendCheck} disabled={sending} className="mt-3 w-full bg-[#181818] text-white hover:bg-black" data-testid="email-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Envoyer (tracé dans l'historique)</>}
              </Button>
              {datesRequired && !hasDates && (
                <div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-[#8A6D00]" data-testid="email-dates-required">
                  <AlertTriangle className="h-3 w-3 shrink-0" /> Sélectionnez au moins une date de passage pour pouvoir envoyer.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-3 shadow-[var(--shadow-sm)]">
            <div className="mb-2 flex items-center gap-2 px-1 text-sm font-bold"><Mail className="h-4 w-4 text-[var(--shariti-orange)]" /> Aperçu</div>
            {html ? (
              <iframe title="preview" srcDoc={html} className="h-[520px] w-full rounded-xl border border-[var(--border-soft)]" data-testid="email-preview" />
            ) : (
              <div className="grid h-[520px] place-items-center rounded-xl bg-[var(--surface-2)] text-sm text-muted-ink">L'aperçu de l'email apparaîtra ici.</div>
            )}
          </div>

          {spot && (
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4">
              <h3 className="mb-2 text-sm font-bold">Historique des envois — {spot.normalized_name}</h3>
              {history.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-ink">Aucun email envoyé pour ce spot.</div>
              ) : (
                <div className="space-y-2">
                  {history.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-2 rounded-xl border border-[var(--border-soft)] px-3 py-2 text-xs" data-testid={`email-log-${e.id}`}>
                      <div className="min-w-0"><div className="font-bold">{e.subject}</div>
                        <div className="text-muted-ink">{TYPE_LABELS[e.type] || e.type} • {e.to} • {frenchDateTime(e.sent_at)} • <span className="font-semibold">{e.provider}</span></div>
                        {e.status === "failed" && e.error && (
                          <div className="mt-1 text-[11px] leading-snug text-[#9B2226]" data-testid={`email-error-${e.id}`}>{e.error}</div>
                        )}
                        {e.delivery_status && DELIVERY_META[e.delivery_status] && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`email-delivery-${e.id}`}>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${DELIVERY_META[e.delivery_status].cls}`}>
                              {(() => { const I = DELIVERY_META[e.delivery_status].icon; return <I className="h-3 w-3" />; })()}
                              {DELIVERY_META[e.delivery_status].label}
                            </span>
                            {e.delivery_status === "bounced" && (
                              <button type="button" onClick={focusContactEmail}
                                className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)]"
                                data-testid={`email-fix-${e.id}`}>
                                <RotateCcw className="h-3 w-3" /> Corriger l'adresse et renvoyer
                              </button>
                            )}
                            {e.delivery_detail && (
                              <span className="w-full text-[10px] leading-snug text-muted-ink">{e.delivery_detail}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {e.status === "failed" ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(214,69,69,0.12)] px-2 py-0.5 font-semibold text-[#9B2226]" data-testid={`email-failed-${e.id}`}><AlertTriangle className="h-3.5 w-3.5" /> Échec d'envoi</span>
                      ) : e.type === "attestation" ? (
                        <span className="inline-flex shrink-0 items-center gap-1 text-[#1F9D55]"><ShieldCheck className="h-3.5 w-3.5" /> Preuve envoyée</span>
                      ) : e.response_ok ? (
                        <span className="inline-flex shrink-0 items-center gap-1 text-[#1F9D55]"><MailCheck className="h-3.5 w-3.5" /> OK reçu</span>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => markConfirmed(e.id)} className="h-7 shrink-0 text-xs" data-testid={`mark-ok-${e.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Marquer OK</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {spot && acks.length > 0 && (
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid="ack-trace-card">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold"><ShieldCheck className="h-4 w-4 text-[var(--shariti-orange)]" /> Accusés & propositions — {spot.normalized_name}</h3>
              <div className="space-y-2">
                {acks.map((a) => {
                  const kind = a.kind || "confirmation";
                  const isProp = kind === "proposition";
                  const title = isProp ? "Proposition de dates" : (a.association_acronym || a.association_name || "Confirmation");
                  return (
                  <div key={a.id} className="rounded-xl border border-[var(--border-soft)] px-3 py-2 text-xs" data-testid={`ack-trace-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold text-ink">{title}{a.dates ? ` • ${a.dates}` : ""}</div>
                        <div className="text-muted-ink">Créé le {frenchDateTime(a.created_at)} par {a.created_by || a.cdp_name || "—"}</div>
                      </div>
                      {a.status === "confirmed" ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 font-semibold text-[#0F5A2F]" data-testid={`ack-badge-${a.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Confirmé</span>
                      ) : a.status === "countered" ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(238,114,46,0.14)] px-2 py-0.5 font-semibold text-[#8A3A10]" data-testid={`ack-badge-${a.id}`}><Handshake className="h-3.5 w-3.5" /> Contre-proposition</span>
                      ) : a.status === "proposed" ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(233,184,0,0.15)] px-2 py-0.5 font-semibold text-[#5A4200]" data-testid={`ack-badge-${a.id}`}><CalendarRange className="h-3.5 w-3.5" /> Envoyée</span>
                      ) : (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(233,184,0,0.15)] px-2 py-0.5 font-semibold text-[#5A4200]" data-testid={`ack-badge-${a.id}`}><Clock className="h-3.5 w-3.5" /> En attente</span>
                      )}
                    </div>

                    {/* Contre-proposition reçue : dates du magasin + actions Approuver / Reproposer */}
                    {isProp && a.status === "countered" && (
                      <div className="mt-2 rounded-lg border border-[rgba(238,114,46,0.28)] bg-[#FFF7F1] px-2.5 py-2" data-testid={`ack-counter-${a.id}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A3A10]">Le magasin propose</div>
                        <div className="mt-0.5 text-[13px] font-bold text-ink">{a.counter_dates || "Dates transmises"}</div>
                        {a.counter_note && <div className="mt-1 text-[11px] italic text-muted-ink">« {a.counter_note} »</div>}
                        {reproposeFor === a.id ? (
                          <div className="mt-2 space-y-2" data-testid={`ack-repropose-form-${a.id}`}>
                            <DatesPicker value={rpDates} onChange={setRpDates} onDatesChange={setRpIso}
                              label={null} testId={`ack-rp-dates-${a.id}`}
                              helperText="Nouvelles dates à proposer au directeur." />
                            <Textarea value={rpNote} onChange={(e) => setRpNote(e.target.value)}
                              placeholder="Message (facultatif)…" rows={2} className="bg-white text-xs"
                              data-testid={`ack-rp-note-${a.id}`} />
                            <div className="flex gap-1.5">
                              <Button size="sm" onClick={() => submitRepropose(a.id)} disabled={ackBusy === a.id}
                                className="h-8 flex-1 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"
                                data-testid={`ack-rp-send-${a.id}`}>
                                {ackBusy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="mr-1.5 h-3.5 w-3.5" /> Envoyer</>}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setReproposeFor(null)} disabled={ackBusy === a.id}
                                className="h-8 border-[var(--border-soft)] bg-white" data-testid={`ack-rp-cancel-${a.id}`}>
                                Annuler
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex gap-1.5">
                            <Button size="sm" onClick={() => approveAck(a.id)} disabled={ackBusy === a.id}
                              className="h-8 flex-1 bg-[#1F9D55] text-white hover:bg-[#178047]"
                              data-testid={`ack-approve-${a.id}`}>
                              {ackBusy === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ThumbsUp className="mr-1.5 h-3.5 w-3.5" /> Approuver</>}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openRepropose(a.id)} disabled={ackBusy === a.id}
                              className="h-8 flex-1 border-[rgba(238,114,46,0.4)] bg-white text-[#8A3A10] hover:bg-[#FFF3E9]"
                              data-testid={`ack-repropose-${a.id}`}>
                              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reproposer
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Proposition en attente de réponse du directeur */}
                    {isProp && a.status === "proposed" && (
                      <div className="mt-1.5 text-[11px] text-muted-ink" data-testid={`ack-waiting-${a.id}`}>
                        En attente de la réponse du directeur (valider ou proposer d'autres dates).
                      </div>
                    )}

                    {/* Accord confirmé : preuve + attestation */}
                    {a.status === "confirmed" && (
                      <div className="mt-1.5 rounded-lg bg-[rgba(31,157,85,0.06)] px-2 py-1.5 text-[11px] text-[rgba(24,24,24,0.7)]">
                        <span className="font-semibold text-[#0F5A2F]">{a.director_name}</span>
                        {a.director_email ? ` · ${a.director_email}` : ""}
                        {a.director_fonction ? ` · ${a.director_fonction}` : ""}
                        <div className="text-muted-ink">Le {frenchDateTime(a.confirmed_at)} · IP {a.ip || "—"}</div>
                        {a.token && (
                          <a href={`${API}/ack/public/${a.token}/attestation.pdf`} target="_blank" rel="noreferrer"
                            className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-[rgba(31,157,85,0.4)] bg-white px-2.5 py-1 font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.08)]"
                            data-testid={`ack-attestation-${a.id}`}>
                            <FileDown className="h-3.5 w-3.5" /> Télécharger l'attestation (PDF)
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* #1 — Historique des appels/audios du spot (chronologie + résumé IA) */}
          {spot && (
            <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4" data-testid="calls-history-card">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold">
                <AudioLines className="h-4 w-4 text-[var(--shariti-orange)]" /> Historique des appels — {spot.normalized_name}
              </h3>
              {calls.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-ink">Aucun appel enregistré pour ce spot.</div>
              ) : (
                <div className="relative space-y-3 border-l-2 border-[var(--border-soft)] pl-4">
                  {calls.map((c) => {
                    const inbound = c.direction === "inbound";
                    const vm = c.status === "voicemail" || c.outcome === "voicemail";
                    const Icon = vm ? Voicemail : inbound ? PhoneIncoming : PhoneOutgoing;
                    const dur = c.duration_sec
                      ? `${Math.floor(c.duration_sec / 60)}:${String(Math.round(c.duration_sec % 60)).padStart(2, "0")}`
                      : null;
                    return (
                      <div key={c.id} className="relative" data-testid={`call-item-${c.id}`}>
                        <span className="absolute -left-[22px] top-0.5 grid h-4 w-4 place-items-center rounded-full bg-[var(--shariti-orange)] text-white">
                          <Icon className="h-2.5 w-2.5" />
                        </span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                          <span className="font-semibold text-ink">{frenchDateTime(c.started_at)}</span>
                          <span className="text-muted-ink">· {inbound ? "Entrant" : "Sortant"}</span>
                          {dur && <span className="text-muted-ink">· {dur}</span>}
                          {c.decideur && <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 font-medium text-muted-ink">{c.decideur}</span>}
                        </div>
                        {c.ai_summary ? (
                          <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-[rgba(24,24,24,0.78)]" data-testid={`call-summary-${c.id}`}>{c.ai_summary}</p>
                        ) : c.transcript ? (
                          <p className="mt-0.5 line-clamp-2 text-[12px] italic leading-snug text-muted-ink">{c.transcript}</p>
                        ) : (
                          <p className="mt-0.5 text-[11px] italic text-muted-ink">Pas de résumé disponible.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fenêtre de vérification avant envoi (double-contrôle adresse + dates) */}
      <Dialog open={sendCheckOpen} onOpenChange={setSendCheckOpen}>
        <DialogContent className="max-w-md" data-testid="send-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[var(--shariti-orange)]" /> Vérification avant envoi
            </DialogTitle>
            <DialogDescription>
              Vérifiez bien le destinataire et les dates : un envoi vers une mauvaise adresse revient en « non délivré ».
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const c = spot?.contact_principal || {};
            const contactName = [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim();
            const suspicion = emailSuspicion(contactEmail);
            return (
              <div className="space-y-3">
                <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink"><UserRound className="h-3 w-3" /> Destinataire</div>
                  {(contactName || c.fonction) && (
                    <div className="mt-1 text-sm font-semibold text-ink" data-testid="send-check-contact">
                      {contactName || "—"}{c.fonction ? <span className="font-normal text-muted-ink"> · {c.fonction}</span> : null}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-1.5 break-all text-base font-extrabold text-[var(--shariti-orange)]" data-testid="send-check-email">
                    <Mail className="h-4 w-4 shrink-0" /> {contactEmail.trim() || "—"}
                  </div>
                  {suspicion && (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[rgba(233,168,0,0.16)] px-2 py-1 text-[11px] font-medium text-[#7A5B00]" data-testid="send-check-email-warning">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {suspicion} Vérifiez bien avant d'envoyer.
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl border border-[var(--border-soft)] p-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Type d'email</div>
                    <div className="mt-0.5 font-semibold text-ink">{TYPE_LABELS[type] || type}</div>
                  </div>
                  <div className="rounded-xl border border-[var(--border-soft)] p-2">
                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink"><CalendarClock className="h-3 w-3" /> Dates de passage</div>
                    <div className="mt-0.5 font-semibold text-ink" data-testid="send-check-dates">{(facts.dates || "").trim() || (isoDates.length ? `${isoDates.length} date(s)` : "—")}</div>
                  </div>
                </div>
                <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-[var(--border-soft)] p-2.5 text-xs transition-colors hover:bg-[var(--surface-2)]" data-testid="send-check-confirm-email-row">
                  <Checkbox checked={confirmEmailOk} onCheckedChange={(v) => setConfirmEmailOk(!!v)} className="mt-0.5" data-testid="send-check-confirm-email" />
                  <span>Je confirme que <b>l'adresse email</b> ci-dessus est correcte.</span>
                </label>
                {datesRequired && (
                  <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-[var(--border-soft)] p-2.5 text-xs transition-colors hover:bg-[var(--surface-2)]" data-testid="send-check-confirm-dates-row">
                    <Checkbox checked={confirmDatesOk} onCheckedChange={(v) => setConfirmDatesOk(!!v)} className="mt-0.5" data-testid="send-check-confirm-dates" />
                    <span>Je confirme que <b>les dates de passage</b> ci-dessus sont correctes.</span>
                  </label>
                )}
              </div>
            );
          })()}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setSendCheckOpen(false)} data-testid="send-check-cancel">Annuler</Button>
            <Button onClick={doSend} disabled={sending || !confirmEmailOk || (datesRequired && !confirmDatesOk)}
              className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="send-check-confirm">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Confirmer et envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
